import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Observable, firstValueFrom } from 'rxjs';

import { RestapiService } from '@app/services/restapi.service';
import { TimezoneEntityHelper } from '@main-application/management/components/system-configuration/components/timezone-entity.helper';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

import { SendInspectionComponent } from '../components/send-inspection/send-inspection.component';
import {
  RestInspectionContents,
  RestInspectionCreateModel,
  RestInspectionsModel,
  SendInspectionReportRequest,
} from '../models/rest-inspections-model.interface';

@Injectable()
export class InspectionService {
  private _currentSurvey: RestInspectionContents | undefined;

  get survey() {
    return this._currentSurvey;
  }

  constructor(
    private readonly snackbarService: SnackbarService,
    private restApiService: RestapiService,
    private readonly matDialog: MatDialog
  ) {}

  private static fixTimezoneForRestInspectionsModelList(data: RestInspectionsModel[], timezone: number) {
    data.forEach(e => TimezoneEntityHelper.fixTimezoneForRestInspectionsModelToClient(e, timezone));
    return data;
  }

  list() {
    return this.restApiService.getData<RestInspectionsModel[]>(
      `inspections/list?doNotReturnContent=true`,
      InspectionService.fixTimezoneForRestInspectionsModelList
    );
  }

  updateCalendarView(inspectionModel: RestInspectionsModel) {
    return this.restApiService.post<void>(
      `inspections/updateCalendarView/${inspectionModel.id}`,
      {
        calendarPosition: inspectionModel.calendarPosition,
        dueDate: inspectionModel.dueDate,
      },
      TimezoneEntityHelper.fixTimezoneForRestInspectionsModelToServer
    );
  }

  create(inspectionModel: RestInspectionCreateModel): Observable<RestInspectionsModel> {
    return this.restApiService.create<RestInspectionsModel>(`inspections/add`, inspectionModel);
  }

  delete(inspectionId: number): Observable<any> {
    return this.restApiService.delete<void>(`inspections/${inspectionId}`);
  }

  getById(inspectionId: number) {
    return this.restApiService.getData<RestInspectionsModel>(`inspections/${inspectionId}`);
  }

  getBeUnitId(unitId: number) {
    return this.restApiService.getData<RestInspectionsModel[]>(`inspections/ByUnit/${unitId}`);
  }

  async openSendDialog(id: number, inspectionTitle: string) {
    const config: MatDialogConfig = {
      minWidth: '500px',
      panelClass: 'send-inspection-report-modal-container',
      data: inspectionTitle,
    };

    const sendRequest = await firstValueFrom(
      this.matDialog
        .open<SendInspectionComponent, typeof config, SendInspectionReportRequest>(SendInspectionComponent, config)
        .afterClosed()
    );

    if (sendRequest) {
      this.sendReport(id, sendRequest).subscribe({
        next: () => this.snackbarService.success(SnackbarErrorMessage.InspectionReportSentSuccessfully),
        error: error => {
          this.snackbarService.error(SnackbarErrorMessage.ErrorSendingInspectionReport);
          console.error(error);
        },
      });
    }
  }

  sendReport(inspectionId: number, request: SendInspectionReportRequest) {
    return this.restApiService.post<RestInspectionsModel>(`inspections/${inspectionId}/sendReport`, request);
  }
}
